// btw-front/src/components/sections/PartnerLoginCard.jsx
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { message } from "antd";
import Container from "../ui/Container";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import { usePartnerSession } from "../../context/PartnerSessionContext";
import { login as apiLogin } from "../../api/auth";

export default function PartnerLoginCard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { role, profile, signIn, lastIntent, setLastIntent } = usePartnerSession();

  const [loginId, setLoginId] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const isLoggedIn = role !== "guest";

  async function handleSubmit(e) {
    e.preventDefault();
    if (submitting) return;
    setError("");
    setSubmitting(true);
    try {
      const res = await apiLogin(loginId, password);
      // res = { accessToken, expiresAt, role, profile }
      signIn(res.role, res.profile, res.accessToken);
      message.success(
        res.role === "admin" ? t("partners.login.toAdmin") : t("partners.login.toDashboard"),
      );
      const dest = lastIntent || (res.role === "admin" ? "/admin" : "/partners/dashboard");
      setLastIntent(null);
      navigate(dest, { replace: true });
    } catch (err) {
      const msg =
        (err && err.payload && err.payload.error && err.payload.error.message) ||
        t("partners.login.invalid");
      setError(msg);
      message.error(msg);
    } finally {
      setSubmitting(false);
    }
  }

  function goByRole() {
    if (role === "admin") navigate("/admin");
    else if (role === "pending") navigate("/partners/pending");
    else navigate("/partners/dashboard");
  }

  return (
    <section className="bg-slate-100 pt-12 pb-16 md:pt-16 md:pb-24 lg:pt-20">
      <Container>
        <div className="grid gap-6 md:grid-cols-2">
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm md:p-10">
            <h2 className="text-xl font-bold text-slate-900 md:text-2xl">
              {t("partners.login.title")}
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-slate-600">
              {t("partners.login.subtitle")}
            </p>

            {isLoggedIn ? (
              <div className="mt-6 flex flex-col gap-3">
                <p className="text-sm text-slate-700">
                  {t("partners.login.alreadyLoggedIn")}
                  {profile?.name ? ` (${profile.name})` : ""}
                </p>
                <div>
                  <Button variant="accent" onClick={goByRole}>
                    {role === "admin"
                      ? t("partners.login.toAdmin")
                      : t("partners.login.toDashboard")}
                  </Button>
                </div>
              </div>
            ) : (
              <form className="mt-6 flex flex-col gap-3" onSubmit={handleSubmit}>
                <Input
                  label={t("partners.login.loginId")}
                  name="loginId"
                  autoComplete="username"
                  placeholder={t("partners.login.loginIdPlaceholder")}
                  value={loginId}
                  onChange={(e) => setLoginId(e.target.value)}
                  error={error || undefined}
                />
                <Input
                  label={t("partners.login.password")}
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  placeholder={t("partners.login.passwordPlaceholder")}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <div className="mt-2 flex flex-wrap items-center gap-3">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="btw-btn-accent disabled:opacity-60"
                  >
                    {submitting ? t("partners.login.submitting") : t("partners.login.submit")}
                  </button>
                  <Link
                    to="/password-reset"
                    className="text-xs text-slate-500 underline-offset-2 hover:text-accent hover:underline"
                  >
                    {t("partners.login.forgotPassword")}
                  </Link>
                </div>
              </form>
            )}
          </div>

          <div className="rounded-2xl border border-dashed border-slate-300 bg-white/60 p-6 md:p-10">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">
              {t("partners.login.signupPrompt")}
            </p>
            <h3 className="mt-2 text-lg font-bold text-slate-900">{t("partners.signup.title")}</h3>
            <p className="mt-2 text-sm leading-relaxed text-slate-600">
              {t("partners.signup.desc")}
            </p>
            <div className="mt-5">
              <Link to="/partners/signup" className="btw-btn-ghost">
                {t("partners.login.signupLink")} →
              </Link>
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
}
